/**
 * Store Manager - Centralized store state management
 * Handles clearing all stores on logout, user change, or tab close
 */

import { useReportsStore } from "./reportsStore"
import { useChatStore } from "./chatStore"
import { useUIStore } from "./uiStore"
import { useOrganizationStore } from "./organizationStore"

export interface StoreManager {
	clearAllStores: () => void
	clearUserDataStores: () => void
	clearSessionStores: () => void
	setLogoutInProgress: (value: boolean) => void
	saveAllPendingData: () => Promise<void>
}

// Flag to track logout state to prevent beforeunload confirmation during logout
let isLogoutInProgress = false
const debugLog = (...args: unknown[]) => {
	if (import.meta.env.DEV) console.log(...args)
}

/**
 * Clear all persisted store data from localStorage
 */
const clearAllPersistedData = () => {
	if (typeof window !== "undefined") {
		const storeKeys = ["reports-store", "chat-store", "ui-store", "locale-store", "organization-store"]

		storeKeys.forEach((key) => {
			localStorage.removeItem(key)
		})

		debugLog("🗑️ Cleared all persisted store data from localStorage")
	}
}

/**
 * Clear user-specific persisted data, preserve UI preferences
 */
const clearUserPersistedData = () => {
	if (typeof window !== "undefined") {
		const userDataKeys = [
			"reports-store",
			"chat-store",
			"organization-store",
			// Keep 'ui-store' and 'locale-store' for user preferences
		]

		userDataKeys.forEach((key) => {
			localStorage.removeItem(key)
		})

		debugLog("🗑️ Cleared user persisted data from localStorage")
	}
}

/**
 * Clear all stores - complete reset
 */
const clearAllStores = () => {
	debugLog("🧹 Clearing all stores...")

	// Clear in-memory state
	useReportsStore.getState().clearAllData()
	useChatStore.getState().clearAllData()
	useUIStore.getState().clearAllData()
	useOrganizationStore.getState().clearStore()

	// Clear all persisted data
	clearAllPersistedData()

	debugLog("✅ All stores cleared")
}

/**
 * Clear only user-specific data stores
 * Preserves UI preferences like the sidebar state and locale
 */
const clearUserDataStores = () => {
	debugLog("🧹 Clearing user data stores...")

	// Clear in-memory state
	useReportsStore.getState().clearAllData()
	useChatStore.getState().clearAllData()
	useUIStore.getState().clearUserData()
	useOrganizationStore.getState().clearStore()

	// Clear user-specific persisted data
	clearUserPersistedData()

	debugLog("✅ User data stores cleared")
}

/**
 * Clear session-specific stores
 * Preserves user preferences and some UI state
 */
const clearSessionStores = () => {
	debugLog("🧹 Clearing session stores...")

	// Clear chat conversations (session specific)
	useChatStore.getState().clearAllData()

	// Clear UI notifications and modals
	useUIStore.getState().clearSessionData()

	debugLog("✅ Session stores cleared")
}

/**
 * Save all pending data across all stores before logout
 */
const saveAllPendingData = async (): Promise<void> => {
	debugLog("💾 Saving all pending data...")

	try {
		// Save data in all stores that might have pending operations
		await Promise.all([
			useReportsStore.getState().saveAllPendingData(),
			// Add other stores' saveAllPendingData methods here as needed
		])

		debugLog("✅ All pending data saved successfully")
	} catch (error) {
		console.error("❌ Error saving pending data:", error)
		// Continue with logout even if save fails
	}
}

export const storeManager: StoreManager = {
	clearAllStores,
	clearUserDataStores,
	clearSessionStores,
	setLogoutInProgress: (value: boolean) => {
		isLogoutInProgress = value
	},
	saveAllPendingData,
}

/**
 * Manual store cleanup - use this if you need to clear stores programmatically
 * outside of the normal logout/user change flow
 */
export const clearStoresManually = {
	/** Complete reset of all stores */
	all: clearAllStores,
	/** Clear user data but preserve preferences */
	userData: clearUserDataStores,
	/** Clear session data only */
	session: clearSessionStores,
	/** Clear only localStorage (no in-memory state) */
	localStorage: clearAllPersistedData,
	/** Clear only user data from localStorage */
	userLocalStorage: clearUserPersistedData,
}

/**
 * Setup browser event listeners for tab close/refresh
 */
export const setupStoreCleanup = () => {
	// Handle page unload (tab close/refresh)
	const handleBeforeUnload = (event: BeforeUnloadEvent) => {
		// Clear session-specific data but preserve user preferences
		clearSessionStores()

		// Don't show confirmation dialog during logout
		if (isLogoutInProgress) {
			return
		}

		// Optional: Show confirmation dialog for unsaved changes
		const hasUnsavedChanges = useReportsStore.getState().hasUnsavedChanges()
		if (hasUnsavedChanges) {
			event.preventDefault()
			return "You have unsaved changes. Are you sure you want to leave?"
		}
	}

	// Handle page unload without user interaction
	const handleUnload = () => {
		// Clear session data on unload
		clearSessionStores()
	}

	// Handle visibility change (tab switching, minimizing)
	const handleVisibilityChange = () => {
		if (document.hidden) {
			// Tab became hidden - could clear sensitive session data
			debugLog("Tab hidden - clearing sensitive session data")
		} else {
			// Tab became visible - could refresh data
			debugLog("Tab visible - refreshing if needed")
		}
	}

	// Add event listeners
	window.addEventListener("beforeunload", handleBeforeUnload)
	window.addEventListener("unload", handleUnload)
	document.addEventListener("visibilitychange", handleVisibilityChange)

	// Return cleanup function
	return () => {
		window.removeEventListener("beforeunload", handleBeforeUnload)
		window.removeEventListener("unload", handleUnload)
		document.removeEventListener("visibilitychange", handleVisibilityChange)
	}
}
